import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleGenerationRow } from '../../types/articleGeneration';
import { articleGenerationService } from '../../services/articleGenerationService';

interface Props {
  run: ArticleGenerationRow;
  onPatched: (patch: Partial<ArticleGenerationRow>) => void;
  /** Start a full article that keeps this plan's headings (as edited here). */
  onWriteArticle?: () => void;
}

/**
 * Editable plan: headings (headings mode) or headings + briefs (headings_briefs,
 * and a full-article run paused at the human checkpoint). Edits persist.
 */
const HeadingsResult: React.FC<Props> = ({ run, onPatched, onWriteArticle }) => {
  const { t } = useTranslation();
  const isBriefs = run.mode !== 'headings';
  const h1 = isBriefs ? run.briefs?.h1 : run.structure?.h1;
  const rows: { heading: string; brief?: string }[] = isBriefs
    ? (run.briefs?.sections ?? [])
    : (run.structure?.headings ?? []).map((heading) => ({ heading }));

  const commit = async (nextH1: string, nextRows: { heading: string; brief?: string }[]) => {
    if (isBriefs) {
      const briefs = {
        h1: nextH1,
        sections: nextRows.map((r) => ({ heading: r.heading, brief: r.brief ?? '' })),
      };
      // The writer and the critique loop read headings from `structure`, so it
      // must follow every edit made here.
      const structure = { h1: nextH1, headings: nextRows.map((r) => r.heading) };
      await articleGenerationService.updateBriefs(run.id, briefs);
      await articleGenerationService.updateStructure(run.id, structure);
      onPatched({ briefs, structure });
    } else {
      const structure = { h1: nextH1, headings: nextRows.map((r) => r.heading) };
      await articleGenerationService.updateStructure(run.id, structure);
      onPatched({ structure });
    }
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= rows.length) return;
    const next = [...rows];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    commit(h1 ?? '', next);
  };

  if (h1 === undefined || h1 === null) return null;

  return (
    <div className="space-y-3">
      {onWriteArticle && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
          <span className="text-sm text-blue-900">{t('article.write_article_from_plan_hint')}</span>
          <button
            type="button"
            onClick={onWriteArticle}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700"
          >
            {t('article.write_article_from_plan')}
          </button>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium mb-1">{t('article.h1_label')}</label>
        <input
          defaultValue={h1}
          onBlur={(e) => e.target.value !== h1 && commit(e.target.value, rows)}
          className="w-full p-2 border rounded-md font-semibold"
        />
      </div>
      <ol className="space-y-2">
        {rows.map((row, i) => (
          <li key={i} className="border rounded-md p-3 bg-white">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-sm w-6">{i + 1}.</span>
              <input
                defaultValue={row.heading}
                onBlur={(e) =>
                  e.target.value !== row.heading &&
                  commit(h1, rows.map((r, n) => (n === i ? { ...r, heading: e.target.value } : r)))
                }
                className="flex-grow p-2 border rounded-md"
              />
              <button type="button" aria-label="Move up" onClick={() => move(i, i - 1)} className="px-2 text-gray-500 hover:text-gray-900">↑</button>
              <button type="button" aria-label="Move down" onClick={() => move(i, i + 1)} className="px-2 text-gray-500 hover:text-gray-900">↓</button>
              <button
                type="button"
                aria-label="Remove"
                onClick={() => commit(h1, rows.filter((_, n) => n !== i))}
                className="px-2 text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
            {isBriefs && (
              <textarea
                defaultValue={row.brief ?? ''}
                rows={2}
                aria-label={t('article.brief_label')}
                onBlur={(e) =>
                  e.target.value !== (row.brief ?? '') &&
                  commit(h1, rows.map((r, n) => (n === i ? { ...r, brief: e.target.value } : r)))
                }
                className="mt-2 w-full p-2 border rounded-md text-sm"
              />
            )}
          </li>
        ))}
      </ol>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => commit(h1, [...rows, { heading: '', ...(isBriefs ? { brief: '' } : {}) }])}
          className="px-3 py-1.5 border rounded-md text-sm hover:bg-gray-50"
        >
          {t('article.add_heading')}
        </button>
      </div>
    </div>
  );
};

export default HeadingsResult;
